import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ProfileData } from "./ProfileValues";
import { getUser } from "../clients/getUser";
import { getAnonymousToken } from "../clients/getAnonymousToken";
import { AuthConfiguration } from "./types";
import { login } from "../clients/login";
import { verifyEmail } from "../clients/verifyEmail";

type AuthContextType = {
  authHeader: string | undefined;
  userDetails: ProfileData | null;
  isAuthenticated: boolean;
  logout: ({ returnTo }: LogoutParams) => void;
  loginWithEmail: (emailId: string, password: string, accessToken: string) => void;
  loginWithRedirect: ({ returnTo }: LoginWithRedirectParams) => void;
  loginWithEmailToken: (emailVerificationToken: string) => void;
  resetUser: () => Promise<void>;
};

interface LogoutParams {
  returnTo: string;
}

interface LoginWithRedirectParams {
  returnTo?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuthContext = () => {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return value;
};

export interface AuthProviderProps {
  children?: React.ReactNode | React.ReactNode[];
}

const gmgTokenKey = "gmg-access-token";

interface GMGToken {
  type: "user" | "anonymous";
  token: string;
  timestamp: number;
}

export const AuthProvider = (props: AuthProviderProps) => {
  const [authHeader, setAuthHeader] = useState<string | undefined>();
  const [userDetails, setUserDetails] = useState<ProfileData | null>(null);
  const initialTokenString = window.localStorage.getItem(gmgTokenKey);
  const initialToken = initialTokenString
    ? JSON.parse(initialTokenString)
    : null;

  const [token, setToken] = useState<GMGToken | null>(initialToken);

  const isAnonymousToken = () => {
    if (token) {
      return token.type !== "user";
    }
    return true;
  };

  const isAuthenticated = !!token && !isAnonymousToken();

  const logout = useCallback(({ returnTo }: LogoutParams) => {
    window.localStorage.removeItem(gmgTokenKey);
    setToken(null);
    window.location.replace(`${window.location.origin}${returnTo}`);
  }, []);

  const setAllTokens = useCallback(
    (accessToken: string, isAnonymous: boolean = false) => {
      const gmgToken: GMGToken = {
        type: isAnonymous ? "anonymous" : "user",
        token: accessToken,
        timestamp: Date.now(),
      };
      const gmgTokenString = JSON.stringify(gmgToken);
      window.localStorage.setItem(gmgTokenKey, gmgTokenString);
      setToken(gmgToken);
    },
    []
  );

  useEffect(() => {
    (async () => {
      try {
        if (token) {
          const authConfigBearer: AuthConfiguration = {
            authType: "Bearer",
            value: token.token,
          };
          const header = `${authConfigBearer.authType} ${authConfigBearer.value}`;
          setAuthHeader(header);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        if (!token) {
          const anonymousToken = await getAnonymousToken();
          setAllTokens(anonymousToken.token, true);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [token, setAllTokens]);

  useEffect(() => {
    (async () => {
      try {
        if (authHeader && isAuthenticated) {
          const response: any = await getUser(authHeader);
          const userDetails: ProfileData = response.data;
          setUserDetails(userDetails);
        }
      } catch (error: any) {
        console.error(error)
        if (error.status === 401) {
          logout({ returnTo: "/" });
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader]);

  const resetUser = useCallback(async () => {
    if (authHeader) {
      const response: any = await getUser(authHeader);
      const userDetails: ProfileData = response.data;
      setUserDetails(userDetails);
    }
  }, [authHeader]);

  const loginWithEmail = useCallback(
    async (emailId: string, password: string, accessToken: string) => {
      const { token } = await login({ emailId, password, accessToken });
      setAllTokens(token);
    },
    [setAllTokens]
  );

  const loginWithEmailToken = useCallback(
    async (emailVerificationToken: string) => {
      const { token } = await verifyEmail(emailVerificationToken);
      setAllTokens(token);
    },
    [setAllTokens]
  );

  const loginWithRedirect = useCallback(
    ({ returnTo }: LoginWithRedirectParams) => {
      window.location.replace(
        `${window.location.origin}/sign-up${
          returnTo ? "?returnTo=" + returnTo : ""
        }`
      );
    },
    []
  );

  const value = useMemo(() => {
    return {
      isAuthenticated,
      authHeader,
      userDetails,
      logout,
      loginWithEmail,
      loginWithRedirect,
      loginWithEmailToken,
      resetUser,
    };
  }, [
    isAuthenticated,
    authHeader,
    userDetails,
    logout,
    loginWithEmail,
    loginWithRedirect,
    loginWithEmailToken,
    resetUser,
  ]);

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};
